import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Truck, History } from "lucide-react";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function utilizationColor(pct) {
  if (pct >= 100) return "bg-red-100 text-red-800";
  if (pct >= 80) return "bg-emerald-100 text-emerald-800";
  if (pct >= 40) return "bg-blue-100 text-blue-800";
  return "bg-amber-100 text-amber-800";
}

export function Workload() {
  const { data } = useQuery({ queryKey: ["workload"], queryFn: async () => (await api.get("/workload?weeks=4")).data });
  const rows = data?.rows || [];
  const weekStarts = data?.week_starts || [];

  return (
    <div data-testid="workload-page">
      <PageHeader
        eyebrow="Operations Workspace · Workload"
        title="Workload Management"
        description="Users × last 4 weeks. Utilization = logged hours ÷ 40h/week capacity. Colour-coded over/under allocation."
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-x-auto" data-testid="workload-grid">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Employee · Role</th>
                {weekStarts.map((w) => <th key={w} className="px-3 py-3 text-center whitespace-nowrap">Wk of {w.slice(5)}</th>)}
                <th className="px-4 py-3 text-right">Total (h)</th>
                <th className="px-4 py-3 text-right">Avg Util</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r) => (
                <tr key={r.user_id} data-testid={`wl-row-${r.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">{r.designation || roleLabel(r.role)}</div>
                  </td>
                  {r.weeks.map((w) => (
                    <td key={w.week_start} className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono ${utilizationColor(w.utilization_pct)}`}>
                        {w.hours}h · {w.utilization_pct}%
                      </span>
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono">{r.total_hours}</td>
                  <td className="px-4 py-3 text-right"><span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono ${utilizationColor(r.avg_utilization_pct)}`}>{r.avg_utilization_pct}%</span></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={weekStarts.length + 3} className="px-4 py-8 text-center text-sm text-zinc-500">No production hours logged in this window.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Revisions() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", title: "", scope_description: "", initiator: "client", hours_estimated: 0, billable: true, additional_amount: 0 });

  const { data: revs = [] } = useQuery({ queryKey: ["revisions"], queryFn: async () => (await api.get("/revisions")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const pname = (id) => projects.find((p) => p.id === id) || {};

  const createMut = useMutation({
    mutationFn: (p) => api.post("/revisions", p),
    onSuccess: () => {
      toast.success("Revision recorded");
      setOpen(false);
      setForm({ project_id: "", title: "", scope_description: "", initiator: "client", hours_estimated: 0, billable: true, additional_amount: 0 });
      qc.invalidateQueries({ queryKey: ["revisions"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/revisions/${id}/status`, { status }),
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["revisions"] }); },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.project_id || !form.title) return toast.error("Project and title are required");
    createMut.mutate({
      ...form, hours_estimated: Number(form.hours_estimated) || 0,
      additional_amount: Number(form.additional_amount) || 0,
      scope_description: form.scope_description || undefined,
    });
  };

  const STATUSES = ["requested", "in_progress", "delivered", "approved", "rejected"];

  return (
    <div data-testid="revisions-page">
      <PageHeader
        eyebrow="Operations Workspace · Revisions"
        title="Revision Management"
        description="Track every revision round R1..Rn. Billable revisions flow into invoice add-ons."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="new-rev-btn"><Plus className="w-4 h-4 mr-1.5" /> New Revision</Button></SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Record a revision</SheetTitle></SheetHeader>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="new-rev-form">
                <div><Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="rev-project"><SelectValue placeholder="Choose project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="rev-title">Title</Label><Input id="rev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="rev-title" /></div>
                <div><Label htmlFor="rev-scope">Scope</Label><Textarea id="rev-scope" value={form.scope_description} onChange={(e) => setForm({ ...form, scope_description: e.target.value })} data-testid="rev-scope" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Initiator</Label>
                    <Select value={form.initiator} onValueChange={(v) => setForm({ ...form, initiator: v })}>
                      <SelectTrigger data-testid="rev-initiator"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="client">Client</SelectItem><SelectItem value="internal">Internal</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label htmlFor="rev-hrs">Est. hours</Label><Input id="rev-hrs" type="number" value={form.hours_estimated} onChange={(e) => setForm({ ...form, hours_estimated: e.target.value })} data-testid="rev-hours" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-end gap-2"><Checkbox id="rev-bill" checked={form.billable} onCheckedChange={(v) => setForm({ ...form, billable: !!v })} data-testid="rev-billable" /><Label htmlFor="rev-bill">Billable</Label></div>
                  <div><Label htmlFor="rev-amt">Additional Amount</Label><Input id="rev-amt" type="number" value={form.additional_amount} onChange={(e) => setForm({ ...form, additional_amount: e.target.value })} disabled={!form.billable} data-testid="rev-amount" /></div>
                </div>
                <SheetFooter><Button type="submit" disabled={createMut.isPending} data-testid="rev-submit">Record</Button></SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="revisions-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Round</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Initiator</th><th className="px-4 py-3">Billable</th>
                <th className="px-4 py-3 text-right">Est h</th><th className="px-4 py-3 text-right">₹ add</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {revs.map((r) => (
                <tr key={r.id} data-testid={`rev-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs"><History className="w-3 h-3 inline mr-1" />R{r.round_number}</td>
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3 text-zinc-600">{pname(r.project_id).code || "—"}</td>
                  <td className="px-4 py-3 capitalize">{r.initiator}</td>
                  <td className="px-4 py-3">{r.billable ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Billable</Badge> : <Badge variant="outline">Non-billable</Badge>}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.hours_estimated}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{r.billable ? INR(r.additional_amount) : "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={r.status} onValueChange={(v) => statusMut.mutate({ id: r.id, status: v })}>
                      <SelectTrigger className="h-7 w-36" data-testid={`rev-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {revs.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-500">No revisions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Deliveries() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", title: "", description: "", delivered_at: new Date().toISOString().slice(0, 10), format: "PDF", external_link: "" });

  const { data: rows = [] } = useQuery({ queryKey: ["deliveries"], queryFn: async () => (await api.get("/deliveries")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const pcode = (id) => projects.find((p) => p.id === id)?.code || "—";

  const createMut = useMutation({
    mutationFn: (p) => api.post("/deliveries", p),
    onSuccess: () => {
      toast.success("Delivery logged");
      setOpen(false);
      setForm({ project_id: "", title: "", description: "", delivered_at: new Date().toISOString().slice(0, 10), format: "PDF", external_link: "" });
      qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div data-testid="deliveries-page">
      <PageHeader
        eyebrow="Operations Workspace · Deliveries"
        title="Deliveries"
        description="Every deliverable to the client — drawings, renders, models — logged and audited."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="new-del-btn"><Truck className="w-4 h-4 mr-1.5" /> New Delivery</Button></SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Log a delivery</SheetTitle></SheetHeader>
              <form onSubmit={(e) => { e.preventDefault(); if (!form.project_id || !form.title) return toast.error("Project + title required"); createMut.mutate({ ...form, description: form.description || undefined, external_link: form.external_link || undefined, format: form.format || undefined }); }} className="mt-6 space-y-4" data-testid="new-del-form">
                <div><Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="del-project"><SelectValue placeholder="Choose project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="d-title">Title</Label><Input id="d-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="del-title" /></div>
                <div><Label htmlFor="d-desc">Description</Label><Textarea id="d-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="del-desc" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="d-date">Delivered on</Label><Input id="d-date" type="date" value={form.delivered_at} onChange={(e) => setForm({ ...form, delivered_at: e.target.value })} required data-testid="del-date" /></div>
                  <div><Label htmlFor="d-fmt">Format</Label><Input id="d-fmt" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} placeholder="PDF, DWG, RVT" data-testid="del-format" /></div>
                </div>
                <div><Label htmlFor="d-link">External link (Drive / Matterport)</Label><Input id="d-link" value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} data-testid="del-link" /></div>
                <SheetFooter><Button type="submit" disabled={createMut.isPending} data-testid="del-submit">Log Delivery</Button></SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="deliveries-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Delivered on</th><th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Title</th><th className="px-4 py-3">Format</th><th className="px-4 py-3">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((d) => (
                <tr key={d.id} data-testid={`del-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.delivered_at}</td>
                  <td className="px-4 py-3 text-zinc-600">{pcode(d.project_id)}</td>
                  <td className="px-4 py-3 font-medium">{d.title}</td>
                  <td className="px-4 py-3 font-mono text-xs">{d.format || "—"}</td>
                  <td className="px-4 py-3">{d.external_link ? <a href={d.external_link} className="text-xs underline underline-offset-2" target="_blank" rel="noreferrer">Open</a> : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-zinc-500">No deliveries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function OperationsHome() {
  const { data: ops } = useQuery({ queryKey: ["ops-cc"], queryFn: async () => (await api.get("/analytics/operations")).data });
  const t = ops || {};
  return (
    <div data-testid="ops-home">
      <PageHeader
        eyebrow="Operations Workspace"
        title="Operations Command Center"
        description="Today's production, projects at risk, pending approvals and team workload — all in one view."
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-6">
        <div className="grid md:grid-cols-4 gap-4">
          <Card label="Today's Hours" value={t.today?.total_hours ?? "—"} sub={`${t.today?.billable_hours ?? 0} billable`} testid="ops-today" />
          <Card label="Active Projects" value={t.active_project_count ?? "—"} testid="ops-active" />
          <Card label="Pending Handovers" value={t.pending_approvals?.handovers ?? "—"} testid="ops-handovers" />
          <Card label="Hours to Approve" value={t.pending_approvals?.production_hours ?? "—"} testid="ops-pending-hours" />
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <PanelList title="Projects at Risk" items={(t.projects_at_risk || []).map((p) => ({ key: p.id, primary: `${p.code} · ${p.name}`, secondary: p.reason }))} empty="No projects at risk." testid="ops-risk" />
          <PanelList title="Available Designers this week" items={(t.available_designers || []).map((d) => ({ key: d.user_id, primary: d.name, secondary: `${d.utilization_pct}% util · ${d.hours}h logged` }))} empty="No slack capacity." testid="ops-available" />
          <PanelList title="Over-allocated" items={(t.team_workload?.over_allocated || []).map((d) => ({ key: d.user_id, primary: d.name, secondary: `${d.utilization_pct}% util`, tone: "warn" }))} empty="Nobody over-allocated." testid="ops-over" />
          <PanelList title="Under-utilized" items={(t.team_workload?.under_utilized || []).map((d) => ({ key: d.user_id, primary: d.name, secondary: `${d.utilization_pct}% util` }))} empty="Everyone busy enough." testid="ops-under" />
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, sub, testid }) {
  return (
    <div className="border border-zinc-200 rounded-lg bg-white p-5" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-zinc-900">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function PanelList({ title, items, empty, testid, tone }) {
  return (
    <div className="border border-zinc-200 rounded-lg bg-white p-5" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{title}</div>
      <ul className="mt-3 space-y-2">
        {items.map((it) => (
          <li key={it.key} className="flex justify-between items-start text-sm border-b border-zinc-100 pb-2">
            <div className="min-w-0">
              <div className="font-medium truncate">{it.primary}</div>
              <div className="text-[11px] text-zinc-500 truncate">{it.secondary}</div>
            </div>
            {it.tone === "warn" && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">alert</Badge>}
          </li>
        ))}
        {items.length === 0 && <li className="text-xs text-zinc-500">{empty}</li>}
      </ul>
    </div>
  );
}
